import { useEffect, useRef, useState } from 'react';
import {
  ChatFullscreenIcon,
  ChatPauseIcon,
  ChatPlayIcon,
  ChatVolumeOffIcon,
  ChatVolumeOnIcon,
} from '../../../assets/svg';
import { lightTheme } from '../../../styles/theme';
import { ConversationAttachmentEntity } from '../../../types/conversation.types';
import { formatDuration } from '../helpers/chatFormat';
import { createSoloPlayback } from '../hooks/soloPlayback';
import { usePointerSeek } from '../hooks/usePointerSeek';
import {
  Buffered,
  CenterButton,
  Container,
  FullscreenButton,
  MuteButton,
  Played,
  ProgressWrapper,
  Thumb,
  TimeLabel,
  Track,
  UnplayableOverlay,
} from './ChatVideoPlayer.styled';

/**
 * Video attachment player.
 *
 * Adapted from the marketing site's `CustomVideoPlayer`
 * (apps/vgrubs/app/virtual-restaurants) - same custom scrubber and control
 * layout - but the two use cases pull in opposite directions, so this is a
 * separate component rather than a shared one (and the marketing app
 * deliberately keeps its own component tree anyway):
 *
 *   - **No autoplay, no loop.** That one is a muted hero banner; a thread can
 *     hold a dozen videos and having them all start on scroll would be chaos,
 *     never mind the bandwidth on a merchant's tablet.
 *   - **Poster first.** Playback only starts on a click, which means the
 *     browser's autoplay policy lets us play WITH sound - no second click to
 *     unmute, the way the marketing one needs.
 *   - **Aspect ratio comes from the stored `width`/`height`,** not a fixed
 *     16/9. The box is then the right size before a single byte of video
 *     loads, so the message list doesn't reflow - which matters here because
 *     reflow inside the scroll container fights the scroll anchoring that
 *     keeps your place when older pages prepend.
 */

const DEFAULT_RATIO = 16 / 9;

const solo = createSoloPlayback();

/**
 * Hide the fullscreen button outright where neither API exists. A property of
 * the browser, not of any one video, so it is resolved once rather than on
 * every render of every clip in a thread.
 */
const CAN_FULLSCREEN =
  typeof document !== 'undefined' &&
  (!!document.documentElement.requestFullscreen ||
    'webkitEnterFullscreen' in document.createElement('video'));

export const ChatVideoPlayer = ({
  attachment,
  flush,
}: {
  attachment: ConversationAttachmentEntity;
  /** Rendered inside a captioned group, which owns the rounding. */
  flush?: boolean;
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  /** Flips on first play; only drives whether the controls are worth showing. */
  const [started, setStarted] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [duration, setDuration] = useState(
    attachment.durationMs ? attachment.durationMs / 1000 : 0
  );
  const [currentTime, setCurrentTime] = useState(0);
  const [bufferedPct, setBufferedPct] = useState(0);

  /**
   * Only a real `error` event from the element downgrades to a download link.
   *
   * Deliberately NOT gated on `canPlayType(mime)`: that reports support for
   * the *declared MIME type*, and Chrome answers "" for `video/quicktime` even
   * though its MP4 demuxer plays a .mov with H.264 inside perfectly well -
   * which is most of them, since that is what macOS and iOS record. Trusting
   * canPlayType hid working videos behind a download link.
   */
  const [unplayable, setUnplayable] = useState(false);

  const ratio =
    attachment.width && attachment.height
      ? attachment.width / attachment.height
      : DEFAULT_RATIO;

  const pct = duration ? (currentTime / duration) * 100 : 0;

  /**
   * Fullscreen goes on the CONTAINER, not the video, so our own controls come
   * along with it. iPhone Safari is the exception: it refuses fullscreen on a
   * plain div and only exposes `webkitEnterFullscreen` on the video element,
   * which brings up the native player instead - fine, it is the platform's own
   * UI. If neither exists the button hides itself rather than doing nothing.
   */
  const toggleFullscreen = () => {
    const container = containerRef.current;
    const video = videoRef.current as HTMLVideoElement & {
      webkitEnterFullscreen?: () => void;
    };
    if (!container) return;

    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => undefined);
      return;
    }

    if (container.requestFullscreen) {
      container.requestFullscreen().catch(() => undefined);
    } else if (video?.webkitEnterFullscreen) {
      video.webkitEnterFullscreen();
    }
  };

  /**
   * The <video> is always mounted, with `preload="none"` doing the lazy
   * loading instead of a conditional mount. An earlier version mounted it only
   * on first click and called play() from a rAF callback - by which point the
   * ref was not attached yet, so nothing ever started. Calling play() straight
   * out of the click handler also keeps the user-activation gesture, which is
   * what lets it play unmuted.
   */
  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    setStarted(true);
    if (video.paused) video.play().catch(() => undefined);
    else video.pause();
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setMuted(video.muted);
  };

  useEffect(() => {
    const video = videoRef.current;
    // Bound once on mount - the element is always there now, so listeners must
    // not wait for `started` or the very first `play` event would be missed.
    if (!video) return;

    const updateBuffered = () => {
      if (!video.duration) return;
      const { buffered } = video;
      if (buffered.length) {
        const end = buffered.end(buffered.length - 1);
        setBufferedPct((Math.min(end, video.duration) / video.duration) * 100);
      }
    };

    const onLoadedMeta = () => {
      // Prefer the real duration over the stored one once it is known.
      if (video.duration && !Number.isNaN(video.duration)) {
        setDuration(video.duration);
      }
      updateBuffered();
    };
    const onTime = () => {
      setCurrentTime(video.currentTime || 0);
      updateBuffered();
    };
    const onPlay = () => {
      solo.claim(video);
      setPlaying(true);
    };
    const onPause = () => setPlaying(false);
    const onEnded = () => {
      setPlaying(false);
      solo.release(video);
    };
    const onVolumeChange = () => setMuted(video.muted);

    video.addEventListener('loadedmetadata', onLoadedMeta);
    video.addEventListener('durationchange', onLoadedMeta);
    video.addEventListener('timeupdate', onTime);
    video.addEventListener('progress', updateBuffered);
    video.addEventListener('play', onPlay);
    video.addEventListener('playing', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('ended', onEnded);
    video.addEventListener('volumechange', onVolumeChange);

    return () => {
      video.removeEventListener('loadedmetadata', onLoadedMeta);
      video.removeEventListener('durationchange', onLoadedMeta);
      video.removeEventListener('timeupdate', onTime);
      video.removeEventListener('progress', updateBuffered);
      video.removeEventListener('play', onPlay);
      video.removeEventListener('playing', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('ended', onEnded);
      video.removeEventListener('volumechange', onVolumeChange);
      solo.release(video);
    };
  }, []);

  const { seeking, seekHandlers } = usePointerSeek({
    trackRef,
    duration,
    onSeek: (seconds) => {
      const video = videoRef.current;
      if (video) video.currentTime = seconds;
    },
  });

  const controlsVisible = started && !playing;

  return (
    <Container ref={containerRef} $ratio={ratio} $flush={flush}>
      <video
        ref={videoRef}
        playsInline
        /* The client-generated poster means we can skip loading anything at
           all until someone actually presses play. Without one, fall back to
           `metadata` so the browser paints frame 1 instead of a black box. */
        preload={attachment.thumbnailUrl ? 'none' : 'metadata'}
        poster={attachment.thumbnailUrl || undefined}
        src={attachment.url}
        onClick={unplayable ? undefined : togglePlay}
        onError={() => setUnplayable(true)}
      />

      {unplayable ? (
        <UnplayableOverlay
          href={attachment.url}
          target="_blank"
          rel="noreferrer"
          download={attachment.name || true}
        >
          <span>This video can&apos;t play in your browser</span>
          <span>Download</span>
        </UnplayableOverlay>
      ) : (
        <CenterButton
          type="button"
          $visible={!playing}
          onClick={togglePlay}
          aria-label={playing ? 'Pause' : 'Play'}
        >
          {/* Red on both, explicitly: this disc is white, and the same pair of
              icons sits on the voice player's RED button, where they have to be
              white. Neither default suits both, so both call sites say which
              they want. */}
          {playing ? (
            <ChatPauseIcon color={lightTheme.primary} />
          ) : (
            <ChatPlayIcon color={lightTheme.primary} />
          )}
        </CenterButton>
      )}

      {!unplayable && CAN_FULLSCREEN && (
        // Offered before playback too - wanting a proper look at a screenshot
        // recording is exactly when you reach for fullscreen first.
        <FullscreenButton
          type="button"
          $visible={!playing}
          onClick={toggleFullscreen}
          aria-label="Fullscreen"
        >
          <ChatFullscreenIcon />
        </FullscreenButton>
      )}

      {started && !unplayable && (
        <>
          <MuteButton
            type="button"
            $visible={muted || !playing}
            onClick={toggleMute}
            aria-label={muted ? 'Unmute' : 'Mute'}
          >
            {muted ? <ChatVolumeOffIcon /> : <ChatVolumeOnIcon />}
          </MuteButton>

          <TimeLabel $visible={controlsVisible || seeking}>
            {formatDuration(currentTime * 1000)} /{' '}
            {formatDuration(duration * 1000)}
          </TimeLabel>

          <ProgressWrapper
            data-ui="progress"
            $visible={controlsVisible || seeking}
            ref={trackRef}
            role="slider"
            aria-valuemin={0}
            aria-valuemax={duration || 0}
            aria-valuenow={currentTime}
            aria-label="Seek"
            {...seekHandlers}
          >
            <Track>
              <Buffered style={{ width: `${bufferedPct}%` }} />
              <Played style={{ width: `${pct}%` }} />
              <Thumb style={{ left: `${pct}%` }} />
            </Track>
          </ProgressWrapper>
        </>
      )}
    </Container>
  );
};
